import type {
  AccountOnboarding,
  AccountOnboardingStatus,
} from '../types/accountOnboarding.js';

// Pure wizard state-machine helpers for Account Onboarding. Shared by the
// wizard UI (chips + per-row actions) and unit-tested directly. Status is
// monotonic: created → awaiting_page_selection → connected → scoped →
// complete. A failure never regresses the status — it lands in `lastError`
// and the row retries the same step. (The one exception: an expired headless
// connect token regresses the row to 'created' so the AM re-runs Connect.)

export const ONBOARDING_STATUS_ORDER: AccountOnboardingStatus[] = [
  'created',
  'awaiting_page_selection',
  'connected',
  'scoped',
  'complete',
];

export function statusRank(status: AccountOnboardingStatus): number {
  return ONBOARDING_STATUS_ORDER.indexOf(status);
}

// Display status: 'failed' overlays the stored status whenever the last
// attempt errored (and the row isn't already complete).
export type OnboardingDisplayStatus = AccountOnboardingStatus | 'failed';

export function displayStatus(
  record: Pick<AccountOnboarding, 'status' | 'lastError'>,
): OnboardingDisplayStatus {
  if (record.status !== 'complete' && record.lastError) return 'failed';
  return record.status;
}

export const ONBOARDING_STATUS_LABEL: Record<OnboardingDisplayStatus, string> = {
  created: 'Created',
  awaiting_page_selection: 'Awaiting page selection',
  connected: 'Connected',
  scoped: 'Scoped',
  complete: 'Complete',
  failed: 'Failed',
};

// The next action available for a row. Drives which button the wizard
// renders. 'connect' → Meta OAuth; 'select-page' → kyck's headless Facebook
// Page picker; 'select' → ad-account scoping; 'finalize' → link the account;
// 'done' → nothing left.
export type OnboardingNextAction =
  | 'connect'
  | 'select-page'
  | 'select'
  | 'finalize'
  | 'done';

export function nextAction(
  record: Pick<AccountOnboarding, 'status'>,
): OnboardingNextAction {
  switch (record.status) {
    case 'created':
      return 'connect';
    case 'awaiting_page_selection':
      return 'select-page';
    case 'connected':
      return 'select';
    case 'scoped':
      return 'finalize';
    case 'complete':
      return 'done';
  }
}

// Legal transition guard, used by the server before each step's mutation.
// Re-running a step the row already passed is allowed for the callback
// (idempotent re-capture) but the guard itself is strict; callers that allow
// re-entry check `statusRank` instead.
export function canTransition(
  from: AccountOnboardingStatus,
  to: AccountOnboardingStatus,
): boolean {
  return statusRank(to) === statusRank(from) + 1;
}

// Account-first model helpers (rework). An account is connectable from the
// wizard when it has no Zernio profile yet; already-connected accounts are
// shown greyed ("manage from the account page").
export function isAccountConnectable(
  account: Pick<{ zernioProfileId: string | null }, 'zernioProfileId'>,
): boolean {
  return !account.zernioProfileId;
}

// Meta-eligibility gate for onboarding. Only offer an account for a Zernio
// profile / Meta connect when it's flagged for Meta — the "Meta ads" service is
// ticked OR the "Run on Meta" platform flag is on. `runsMeta` is read `?? true`
// to match the app-wide convention (legacy docs without the field are treated
// as on), so this only hides an account affirmatively marked NOT on Meta
// (runsMeta === false) that also lacks the meta_ads service. Pure; structural
// so both the client picker and the server candidate resolver share it.
export function isMetaEligibleForOnboarding(
  account: { services?: readonly string[] | null; runsMeta?: boolean | null },
): boolean {
  const services = account.services ?? [];
  return services.includes('meta_ads') || (account.runsMeta ?? true);
}

// Whether an account may appear in the onboarding pickers AT ALL — Meta-
// eligible AND not cancelled. A cancelled account is dropped entirely (never
// shown greyed), regardless of connection state; 'active' and 'paused' still
// show. This is the "drop filter" shared by both surfaces (the single-wizard
// picker uses it directly, still SHOWING eligible-but-connected accounts greyed;
// isOnboardingCandidate layers the not-already-connected gate on top).
export function isOnboardingListable(
  account: {
    services?: readonly string[] | null;
    runsMeta?: boolean | null;
    status?: string | null;
  },
): boolean {
  return account.status !== 'cancelled' && isMetaEligibleForOnboarding(account);
}

// The onboarding candidate predicate: a listable account (Meta-eligible + not
// cancelled) that's ALSO not yet connected (no Zernio profile). Composes all
// three gates — used by the bulk candidate resolver, where a candidate goes
// straight into the mapping table.
export function isOnboardingCandidate(
  account: {
    services?: readonly string[] | null;
    runsMeta?: boolean | null;
    status?: string | null;
    zernioProfileId: string | null;
  },
): boolean {
  return isOnboardingListable(account) && isAccountConnectable(account);
}

// Resume-by-account: the open (non-complete) onboarding record for a kyck
// Account, if any. Legacy records (no kyckAccountId) never match. When
// several exist (shouldn't happen — create resumes instead of duplicating),
// the newest wins.
export function findOpenOnboardingForAccount<
  T extends Pick<AccountOnboarding, 'status' | 'kyckAccountId' | 'createdAt'>,
>(records: T[], accountId: string): T | null {
  const open = records
    .filter((r) => r.kyckAccountId === accountId && r.status !== 'complete')
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return open[0] ?? null;
}

// Sort for the wizard checklist: incomplete rows first (in creation order,
// newest batch at the top), completed rows last.
export function sortOnboardings<
  T extends Pick<AccountOnboarding, 'status' | 'createdAt' | 'name'>,
>(records: T[]): T[] {
  return [...records].sort((a, b) => {
    const aDone = a.status === 'complete' ? 1 : 0;
    const bDone = b.status === 'complete' ? 1 : 0;
    if (aDone !== bDone) return aDone - bDone;
    if (a.createdAt !== b.createdAt) return b.createdAt.localeCompare(a.createdAt);
    return a.name.localeCompare(b.name);
  });
}

